import { useState } from 'react';
import type { FormEvent } from 'react';

const FORM_ENDPOINT = '/contact.php';

type ContactFormProps = {
  formId: string;
};

export default function ContactForm({ formId }: ContactFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honey = String(fd.get('website_alt') || '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }
    fd.delete('website_alt');

    const body = new URLSearchParams();
    fd.forEach((value, key) => body.append(key, String(value)));

    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes('spam');
      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(
          isSpam ? 'Ihre Anfrage konnte nicht verarbeitet werden.' : String(serverMsg) || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg('Netzwerkfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-sm border border-background-300/60 bg-background-100 p-8 flex flex-col items-center text-center py-16">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400 mb-6">
          <i className="ri-check-line text-3xl"></i>
        </div>
        <h3 className="font-display text-2xl font-bold text-foreground-50">Vielen Dank!</h3>
        <p className="mt-3 text-foreground-400 max-w-sm">
          Ihre Anfrage ist bei uns eingegangen. Wir melden uns zeitnah bei Ihnen.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 text-primary-400 hover:text-primary-300 cursor-pointer whitespace-nowrap"
        >
          Neue Anfrage senden
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-background-300/60 bg-background-100 p-8">
      <form onSubmit={handleSubmit} id={formId} className="flex flex-col gap-5">
        <div>
          <label htmlFor={`${formId}-name`} className="mb-1.5 block text-sm font-medium text-foreground-100">
            Name *
          </label>
          <input
            id={`${formId}-name`}
            name="name"
            required
            maxLength={100}
            placeholder="Ihr Name"
            className="w-full rounded-sm border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-50 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500"
          />
        </div>
        <div>
          <label htmlFor={`${formId}-telefon`} className="mb-1.5 block text-sm font-medium text-foreground-100">
            Telefonnummer
          </label>
          <input
            id={`${formId}-telefon`}
            name="telefon"
            type="tel"
            maxLength={50}
            placeholder="Ihre Telefonnummer"
            className="w-full rounded-sm border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-50 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500"
          />
        </div>
        <div>
          <label htmlFor={`${formId}-email`} className="mb-1.5 block text-sm font-medium text-foreground-100">
            E-Mail *
          </label>
          <input
            id={`${formId}-email`}
            name="email"
            type="email"
            required
            maxLength={100}
            placeholder="ihre@email.de"
            className="w-full rounded-sm border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-50 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500"
          />
        </div>
        <div>
          <label htmlFor={`${formId}-leistung`} className="mb-1.5 block text-sm font-medium text-foreground-100">
            Gewünschte Leistung
          </label>
          <div className="relative">
            <select
              id={`${formId}-leistung`}
              name="leistung"
              className="w-full appearance-none rounded-sm border border-background-300 bg-background-50 px-4 py-3 pr-10 text-sm text-foreground-50 outline-none transition-colors focus:border-primary-500 cursor-pointer"
            >
              <option value="">Bitte wählen…</option>
              <option value="Putz & Stuck">Putz & Stuck</option>
              <option value="Innen- & Außenputz">Innen- & Außenputz</option>
              <option value="Malerarbeiten">Malerarbeiten</option>
              <option value="Estrich">Estrich</option>
              <option value="Fliesenverlegung">Fliesenverlegung</option>
              <option value="Innenausbau">Innenausbau</option>
              <option value="Sonstiges">Sonstiges</option>
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          </div>
        </div>
        <div>
          <label htmlFor={`${formId}-nachricht`} className="mb-1.5 block text-sm font-medium text-foreground-100">
            Projektbeschreibung *
          </label>
          <textarea
            id={`${formId}-nachricht`}
            name="nachricht"
            rows={4}
            required
            maxLength={500}
            placeholder="Beschreiben Sie kurz Ihr Vorhaben…"
            className="w-full resize-none rounded-sm border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-50 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500"
          ></textarea>
          <p className="mt-1 text-right text-xs text-foreground-400">Max. 500 Zeichen</p>
        </div>

        <div className="honeypot-field" aria-hidden="true">
          <label htmlFor={`${formId}-website`}>Website</label>
          <input
            id={`${formId}-website`}
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>

        {status === 'error' && errorMsg && (
          <div className="rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            <i className="ri-error-warning-line mr-2"></i>
            {errorMsg}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center h-12 w-full rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
        >
          {submitting ? 'Wird gesendet…' : 'Unverbindlich anfragen'}
        </button>
        <p className="text-center text-xs text-foreground-400">
          Durch Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäß unserer Datenschutzerklärung zu.
        </p>
      </form>
    </div>
  );
}